import { useEffect, useMemo, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroEditorial from "@/components/landing/HeroEditorial";
import PersonaSection from "@/components/landing/PersonaSection";
import ContactSection from "@/components/landing/ContactSection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PremiumBadge } from "@/components/Badges";
import { ArrowRight, ArrowUpRight, MapPin, Loader2, Trophy, Users, Heart, Hourglass, Clock, Quote } from "lucide-react";
import { labelTalentType, eventTypeMeta } from "@/lib/roleCategories";

const ease = [0.25, 0.1, 0.25, 1];
const reveal = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.8, ease } };

const FAQ_ITEMS = [
  { q: "C'est quoi Helencia ?", a: "Une scène dédiée à la mode : les Talents (mannequins, modèles, performers) y rencontrent les Créateurs (photographes, stylistes, maquilleurs, vidéastes, créateurs de mode, marques) autour de vrais projets — castings, shootings, défilés, concours." },
  { q: "C'est pour qui ?", a: "Pour vous si vous incarnez l'image : exposer votre univers, être casté·e, gagner en visibilité. Pour vous si vous créez : trouver des visages et des équipes, publier vos collaborations, orchestrer vos événements." },
  { q: "Comment un Créateur trouve un Talent ?", a: "Il publie une collaboration en quelques clics, reçoit des candidatures avec books complets, et choisit le Talent dont l'univers résonne avec le sien." },
  { q: "Comment je crée mon portfolio ?", a: "Inscription gratuite, choix d'un style éditorial, upload de vos photos, quelques lignes sur votre univers : en dix minutes votre page est en ligne et partageable par QR code." },
  { q: "Et le Premium ?", a: "Plus de photos, des templates exclusifs, le badge doré et une remontée prioritaire dans les recherches. Sans engagement : 1 mois (29,99 €), 3 mois (49,90 €) ou 1 an (149 €)." },
  { q: "Comment vous joindre ?", a: "Via le formulaire ci-dessous ou par email à hello@helencia.fr — réponse sous 48 h ouvrées." },
];

const TESTIMONIALS = [
  { quote: "Mon book est devenu une vraie page de magazine. Trois castings en un mois, sans agence.", name: "Léa", role: "Mannequin éditoriale · Paris" },
  { quote: "Je compose mes équipes édito ici : mannequin, MUA, styliste. Tout le monde parle la même langue.", name: "Antoine", role: "Photographe mode · Lyon" },
  { quote: "Le concours du mois nous a fait découvrir deux visages qu'on n'aurait jamais trouvés ailleurs.", name: "Maison Nord", role: "Marque indépendante" },
];

export default function Landing() {
  const { loading } = useAuth();
  const [talents, setTalents] = useState([]);
  const [events, setEvents] = useState([]);
  const [contest, setContest] = useState(null);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    api.get("/cms/home").then(({ data }) => setCms(data)).catch(() => setCms(null));
    api.get("/talents/discover", { params: { limit: 8 } }).then(({ data }) => setTalents(Array.isArray(data) ? data : [])).catch(() => setTalents([]));
    api.get("/events", { params: { status: "upcoming" } }).then(({ data }) => setEvents(Array.isArray(data) ? data.slice(0, 6) : [])).catch(() => setEvents([]));
    api.get("/concours").then(({ data }) => setContest((Array.isArray(data) ? data : []).find((c) => c.is_current) || null)).catch(() => setContest(null));
  }, []);

  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [loading]);

  const faces = useMemo(() => talents.filter((t) => t.name).slice(0, 4), [talents]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0F0E0D]"><Loader2 className="w-8 h-8 text-[#C68E59] animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="landing-page">
      <Navbar />
      <HeroEditorial cms={cms} />
      <PersonaSection />
      <FacesSection faces={faces} />
      {contest && <ConcoursTeaser c={contest} />}
      <EventsStrip events={events} />
      <Manifesto />
      <Testimonials />
      <section id="faq" className="py-24 bg-[#FAF3EA]" data-testid="section-faq">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-14 grid lg:grid-cols-12 gap-10">
          <motion.div {...reveal} className="lg:col-span-4">
            <p className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 06</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] text-[#1A1A1A] mt-4">Questions <span className="italic text-[#A26F3E]">fréquentes</span></h2>
          </motion.div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="divide-y divide-[#EADDC9] border-y border-[#EADDC9]" data-testid="faq-accordion">
              {FAQ_ITEMS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                  <AccordionTrigger className="font-serif text-lg sm:text-xl text-[#1A1A1A] text-left py-5 hover:no-underline hover:text-[#A26F3E]" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-[#6E4B2A] leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <ContactSection />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ================= Chapitre 02 — Les visages ================= */
function FacesSection({ faces }) {
  return (
    <section id="featured-models" className="py-24 sm:py-32 bg-[#FFFBF5]" data-testid="section-models">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <motion.div {...reveal} className="lg:col-span-7">
            <p className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 02</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1A1A] mt-4">Les <span className="italic text-[#A26F3E]">visages</span> d'Helencia</h2>
          </motion.div>
          <motion.p {...reveal} className="lg:col-span-5 text-[#6E4B2A] text-sm sm:text-base font-light leading-relaxed lg:pb-2">
            De vrais profils, de vraies pages éditoriales. Chaque talent raconte un univers — et c'est cet univers que les créateurs castent.
          </motion.p>
        </div>
        {faces.length === 0 ? (
          <div className="rounded-none border border-dashed border-[#EADDC9] p-14 text-center text-[#8B8680] text-sm">Les premiers visages arrivent.</div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="faces-grid">
            {faces.map((t, i) => <FaceCard key={t.user_id} t={t} offset={i % 2 === 1} index={i} />)}
          </div>
        )}
        <motion.div {...reveal} className="mt-12 flex justify-end">
          <Link to="/register?role=creator" className="group inline-flex items-center gap-3 text-sm text-[#1A1A1A] hover:text-[#A26F3E] transition-colors" data-testid="faces-see-all">
            Découvrir tous les talents <span className="w-10 h-10 rounded-full border border-[#1A1A1A]/20 group-hover:border-[#C68E59] group-hover:bg-[#C68E59] group-hover:text-white flex items-center justify-center transition-colors"><ArrowRight className="w-4 h-4" /></span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function FaceCard({ t, offset, index }) {
  const [url, setUrl] = useState(optimizeUrl(t.demo_photo_url || t.picture || null, 640));
  useEffect(() => {
    if (t.demo_photo_url) return;
    api.get(`/portfolio/${t.user_id}`).then(({ data }) => {
      const arr = Array.isArray(data) ? data : [];
      const main = arr.find((p) => p.is_main && !p.is_deleted) || arr.find((p) => !p.is_deleted);
      if (main) setUrl(optimizeUrl(`${API}/files/${main.storage_path}`, 640));
    }).catch(() => {});
  }, [t]);
  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: index * 0.08, ease }}
      className={`group flex flex-col ${offset ? "lg:mt-16" : ""}`} data-testid={`model-card-${t.user_id}`}
    >
      <Link to={`/talents/${t.user_id}`} className="relative block aspect-[3/4] overflow-hidden bg-[#FAF3EA]">
        {url ? <img src={url} alt={t.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" /> : <div className="w-full h-full bg-[#EADDC9]" />}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        {t.is_premium && <div className="absolute top-3 right-3"><PremiumBadge /></div>}
        <span className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/90 text-[#1A1A1A] flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500"><ArrowUpRight className="w-4 h-4" /></span>
      </Link>
      <div className="mt-4">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#A26F3E]">{labelTalentType(t.talent_type) || "Talent"}</p>
        <Link to={`/talents/${t.user_id}`} className="font-serif text-xl sm:text-2xl text-[#1A1A1A] mt-1 leading-tight block hover:text-[#A26F3E] transition-colors" data-testid={`model-view-${t.user_id}`}>{t.name}</Link>
        {t.location && <p className="text-xs text-[#8C7D6B] mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{t.location}</p>}
      </div>
    </motion.article>
  );
}

/* ================= Chapitre 03 — Concours du mois ================= */
function ConcoursTeaser({ c }) {
  return (
    <section id="contests" className="bg-[#0F0E0D] text-[#F7F5F0] py-24 sm:py-32" data-testid="section-contests">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div {...reveal} className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <p className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 03 · Concours du mois</p>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#B5AEA5] mt-4 inline-flex items-center gap-2"><Trophy className="w-3.5 h-3.5 text-[#E6C29A]" /> {c.theme}</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.02] mt-3" data-testid="contest-hero-title">{c.title}</h2>
            <p className="text-[#B5AEA5] text-sm sm:text-base font-light leading-relaxed mt-6 line-clamp-3">{c.description}</p>
            <div className="mt-8 grid grid-cols-3 gap-4 border-y border-white/10 py-6">
              <Stat icon={Hourglass} label="jours restants" value={c.days_left ?? "—"} />
              <Stat icon={Users} label="participants" value={c.participants_count ?? 0} />
              <Stat icon={Heart} label="votes" value={c.votes_total ?? 0} />
            </div>
            {c.prize && <p className="mt-6 text-sm text-[#E6C29A]">À la clé : <span className="text-[#F7F5F0]">{c.prize}</span></p>}
            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <Link to={`/concours/${c.id}`} className="inline-flex items-center gap-2 bg-[#C68E59] hover:bg-[#A26F3E] text-[#0F0E0D] hover:text-white text-sm font-semibold px-7 h-12 transition-colors" data-testid="concours-participate-button">Participer <ArrowUpRight className="w-4 h-4" /></Link>
              <Link to={`/concours/${c.id}`} className="text-sm text-[#B5AEA5] hover:text-[#E6C29A] transition-colors" data-testid="contest-hero-see-entries">Voir les participations</Link>
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative aspect-[4/3] lg:aspect-[16/11] overflow-hidden" data-testid="contest-hero-cover">
            {c.image_url && <img src={optimizeUrl(c.image_url, 1280)} alt={c.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0D]/70 to-transparent" />
            <span className="absolute top-5 left-5 bg-white/90 text-[#0F0E0D] text-[10px] uppercase tracking-[0.22em] px-3 py-1.5">Ouvert jusqu'au {c.end_date && new Date(c.end_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })}</span>
            {(c.top_entries || []).length > 0 && (
              <div className="absolute bottom-5 left-5 flex items-center gap-2">
                <div className="flex -space-x-2">{c.top_entries.map((e) => e.user?.picture ? <img key={e.id} src={e.user.picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#0F0E0D]" /> : <span key={e.id} className="w-9 h-9 rounded-full bg-[#C68E59] border-2 border-[#0F0E0D] flex items-center justify-center text-xs font-semibold text-[#0F0E0D]">{(e.user?.name || "?")[0]}</span>)}</div>
                <span className="text-xs text-[#D8D2C8]">en tête du classement</span>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="font-serif text-3xl sm:text-4xl leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#B5AEA5] mt-2 inline-flex items-center gap-1.5"><Icon className="w-3 h-3 text-[#E6C29A]" />{label}</p>
    </div>
  );
}

/* ================= Chapitre 04 — Agenda ================= */
function EventsStrip({ events }) {
  return (
    <section id="events" className="py-24 sm:py-32 bg-[#FAF3EA]" data-testid="section-events">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.div {...reveal} className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 04</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1A1A] mt-4">L'<span className="italic text-[#A26F3E]">agenda</span> mode</h2>
          </div>
          <Link to="/events" className="text-sm text-[#1A1A1A] hover:text-[#A26F3E] inline-flex items-center gap-2 transition-colors" data-testid="events-see-all">Tout l'agenda <ArrowRight className="w-4 h-4" /></Link>
        </motion.div>
        {events.length === 0 ? (
          <div className="border border-dashed border-[#EADDC9] p-14 text-center text-[#8B8680] text-sm">De nouveaux castings et rendez-vous sont annoncés chaque semaine.</div>
        ) : (
          <div className="flex gap-5 overflow-x-auto pb-4 -mx-6 px-6 sm:mx-0 sm:px-0 snap-x" data-testid="events-strip">
            {events.map((e, i) => <EventCard key={e.id} e={e} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function EventCard({ e, index }) {
  const meta = eventTypeMeta(e.event_type);
  const cover = optimizeUrl(e.cover_image ? `${API}/files/${e.cover_image}` : e.image_url, 800);
  const d = e.date ? new Date(e.date) : null;
  return (
    <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: index * 0.07, ease }} className="snap-start shrink-0 w-[78vw] sm:w-[360px]">
      <Link to={`/events/${e.id}`} className="group block bg-white border border-[#EADDC9] hover:border-[#C68E59] transition-colors" data-testid={`event-card-${e.id}`}>
        <div className="relative aspect-[16/10] overflow-hidden bg-[#EADDC9]">
          {cover && <img src={cover} alt={e.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />}
          <span className="absolute top-3 left-3 bg-white/90 text-[#1A1A1A] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">{meta.short}</span>
          {d && <div className="absolute bottom-3 left-3 bg-[#1A1A1A] text-white px-3 py-2 text-center"><div className="font-serif text-xl leading-none">{d.getDate()}</div><div className="text-[9px] uppercase tracking-[0.2em] mt-0.5">{d.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "")}</div></div>}
        </div>
        <div className="p-5">
          <h3 className="font-serif text-xl text-[#1A1A1A] leading-tight line-clamp-2">{e.title}</h3>
          <p className="text-xs text-[#6E4B2A] mt-3 flex items-center gap-3"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>{e.time && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{e.time}</span>}</p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#C68E59] group-hover:gap-2.5 transition-[gap]" data-testid="event-card-detail-button">Découvrir <ArrowUpRight className="w-3.5 h-3.5" /></span>
        </div>
      </Link>
    </motion.div>
  );
}

/* ================= Chapitre 05 — Manifeste ================= */
function Manifesto() {
  return (
    <section id="about" className="py-24 sm:py-32 bg-[#FFFBF5]" data-testid="section-about">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <motion.div {...reveal} className="lg:col-span-5 relative">
            <div className="aspect-[3/4] overflow-hidden"><img src="/landing/styliste.jpg" alt="Backstage" loading="lazy" className="w-full h-full object-cover" /></div>
            <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-[#1A1A1A] text-white px-6 py-5 max-w-[240px]">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#E6C29A]">Le manifeste</p>
              <p className="font-serif text-lg leading-snug mt-2">Pas un annuaire. Une scène.</p>
            </div>
          </motion.div>
          <div className="lg:col-span-7 lg:pt-10">
            <motion.p {...reveal} className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 05</motion.p>
            <motion.blockquote {...reveal} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-[#1A1A1A] mt-5">
              « Helencia n'est pas un site web. C'est le <span className="italic text-[#A26F3E]">foyer créatif</span> de la mode de demain. »
            </motion.blockquote>
            <motion.div {...reveal} className="mt-10 grid sm:grid-cols-2 gap-8 text-sm text-[#6E4B2A] leading-relaxed font-light">
              <p><span className="font-serif text-5xl float-left mr-3 leading-[0.8] text-[#1A1A1A]">N</span>ous avons grandi dans les backstages, les studios et les showrooms. Nous savons qu'une carrière se joue souvent sur une rencontre — un regard croisé, un book ouvert au bon moment.</p>
              <p>Helencia réunit sur une même scène celles et ceux qui incarnent l'image et celles et ceux qui la créent. Sans intermédiaire, sans bruit. Juste des univers qui se reconnaissent et des projets qui naissent.</p>
            </motion.div>
            <motion.div {...reveal} className="mt-10 grid grid-cols-3 gap-4 border-t border-[#EADDC9] pt-8">
              {[["Éditorial", "Chaque profil est une page de magazine"], ["Direct", "Créateurs et talents se parlent sans filtre"], ["Vivant", "Castings, concours et événements chaque mois"]].map(([t, s]) => (
                <div key={t}><p className="font-serif text-xl text-[#1A1A1A]">{t}</p><p className="text-xs text-[#8C7D6B] mt-1.5 leading-relaxed">{s}</p></div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-[#1A1A1A] text-[#F7F5F0] py-24 sm:py-28" data-testid="section-testimonials">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <motion.p {...reveal} className="caption-label text-[#C68E59] tracking-[0.25em] mb-12">Ils ont trouvé leur place</motion.p>
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {TESTIMONIALS.map((t, i) => (
            <motion.figure key={t.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.1, ease }} className="bg-[#1A1A1A] p-8 sm:p-10">
              <Quote className="w-5 h-5 text-[#C68E59]" />
              <blockquote className="font-serif text-xl sm:text-2xl font-light leading-snug mt-5">{t.quote}</blockquote>
              <figcaption className="mt-6 text-xs text-[#B5AEA5]"><span className="text-[#E6C29A]">{t.name}</span> · {t.role}</figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className="relative bg-[#0F0E0D] text-[#F7F5F0] py-28 sm:py-36 overflow-hidden" data-testid="section-final-cta">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none">
        <span className="font-serif text-[22vw] leading-none text-white/[0.04] tracking-tight">HELENCIA</span>
      </div>
      <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#C68E59]/15 blur-[160px] pointer-events-none" />
      <motion.div {...reveal} className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="caption-label text-[#C68E59] tracking-[0.25em]">Dernier chapitre</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.98] mt-5">Vous avez <span className="italic text-[#E6C29A]">votre place</span> ici.</h2>
        </div>
        <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
          <button onClick={() => navigate("/register")} className="inline-flex items-center justify-center gap-2 bg-[#C68E59] hover:bg-[#A26F3E] text-[#0F0E0D] hover:text-white text-sm font-semibold px-8 h-[52px] transition-colors" data-testid="final-cta-register">Créer mon profil gratuitement <ArrowUpRight className="w-4 h-4" /></button>
          <button onClick={() => navigate("/login")} className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#E6C29A] hover:text-[#E6C29A] text-sm px-8 h-[52px] transition-colors" data-testid="final-cta-login">Se connecter</button>
        </div>
      </motion.div>
    </section>
  );
}
